import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, ArrowRight, Upload } from 'lucide-react';
import { useState, useEffect } from 'react';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';
import TypedText from '@/components/TypedText';

const moduleOptions = ['Manage', 'Mandatory Tariff', 'Resources', 'Transactions'];

export default function ReportFormV2Scene({ onNext }) {
  const [nameTyped, setNameTyped] = useState(false);
  const [emailTyped, setEmailTyped] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [moduleSelected, setModuleSelected] = useState(false);
  const [showSubject, setShowSubject] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const [showDesc, setShowDesc] = useState(false);

  useEffect(() => {
    const t = [];
    t.push(setTimeout(() => setNameTyped(true),     1000));
    t.push(setTimeout(() => setEmailTyped(true),    1000));
    t.push(setTimeout(() => setModuleOpen(true),    3200));
    t.push(setTimeout(() => setModuleSelected(true), 4200));
    t.push(setTimeout(() => setModuleOpen(false),   4700));
    t.push(setTimeout(() => setShowSubject(true),   5200));
    t.push(setTimeout(() => setShowFile(true),      6400));
    t.push(setTimeout(() => setShowDesc(true),      7200));
    return () => t.forEach(clearTimeout);
  }, []);

  const cursorPath = [
    { x: 600, y: 150, delay: 0.6 },
    { x: 400, y: 330, delay: 1.2 },          // name field
    { x: 800, y: 330, delay: 1.0 },          // email field
    { x: 400, y: 500, delay: 1.6 },          // module
    { x: 400, y: 680, delay: 2.0 },          // subject
    { x: 800, y: 680, delay: 1.2 },          // file
    { x: 500, y: 820, delay: 2.2 },          // description
    { x: 600, y: 960, delay: 1.6, click: true }, // submit
  ];

  return (
    <PortalShellV2
      variant="headerOnly"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.4} />}
    >
      <div data-testid="scene-report-form-v2" className="px-16 pt-8 pb-10">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[12.5px] font-body text-[#6B7280] mb-2 flex items-center gap-1">
            Home <ChevronRight size={11} /> <span className="text-[#0A0A0A]">Report Issue</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading font-extrabold text-[42px] leading-tight tracking-tight text-[#0A0A0A] mb-2"
          >
            Report Issue
          </motion.h1>

          <div className="border-b border-[#E5E7EB] mb-8" />

          {/* Basic details */}
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading font-extrabold text-[22px] text-[#0A0A0A] mb-5">Basic details</h2>

            <div className="grid grid-cols-2 gap-x-8 gap-y-5 mb-8">
              <div>
                <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">Name</label>
                <motion.div
                  animate={nameTyped ? { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] } : {}}
                  transition={{ duration: 0.8 }}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-body text-[#0A0A0A] min-h-[42px] flex items-center"
                >
                  {nameTyped ? <TypedText text="Abdul Ahmed" delay={0} speed={30} /> : <span className="text-[#9CA3AF]">Abdul Ahmed</span>}
                </motion.div>
              </div>
              <div>
                <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">Email address</label>
                <motion.div
                  animate={emailTyped ? { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] } : {}}
                  transition={{ duration: 0.8 }}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-body text-[#0A0A0A] min-h-[42px] flex items-center"
                >
                  {emailTyped ? <TypedText text="abdulahmed@abc.com" delay={0} speed={22} /> : <span className="text-[#9CA3AF]">abdulahmed@abc.com</span>}
                </motion.div>
              </div>

              <div className="relative">
                <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">Module</label>
                <motion.div
                  animate={{
                    borderColor: moduleOpen ? '#0D9488' : (moduleSelected ? '#0D9488' : '#E5E7EB'),
                    boxShadow: moduleOpen ? '0 0 0 3px rgba(13,148,136,0.18)' : '0 0 0 0 transparent',
                  }}
                  className="w-full px-3.5 py-2.5 rounded-lg border bg-white text-[13px] font-body text-[#0A0A0A] flex items-center justify-between min-h-[42px]"
                >
                  {moduleSelected
                    ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>Mandatory Tariff</motion.span>
                    : <span className="text-[#9CA3AF]">Select module</span>}
                  <ChevronDown size={15} className="text-[#6B7280]" />
                </motion.div>
                <AnimatePresence>
                  {moduleOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="absolute z-20 w-full mt-1 bg-white rounded-lg border border-[#E5E7EB] shadow-xl overflow-hidden"
                    >
                      {moduleOptions.map(opt => (
                        <div key={opt}
                          className={`px-3.5 py-2.5 text-[13px] font-body cursor-pointer ${
                            opt === 'Mandatory Tariff' && moduleSelected
                              ? 'bg-teal-50 text-teal-700 font-medium'
                              : 'text-[#374151] hover:bg-[#FAFAFA]'
                          }`}
                        >{opt}</div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div />
            </div>

            <div className="border-t border-[#E5E7EB] mb-8" />

            <h2 className="font-heading font-extrabold text-[22px] text-[#0A0A0A] mb-5">Complaint</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5 mb-5">
              <div>
                <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">Subject<span className="text-red-500">*</span></label>
                <motion.div
                  animate={showSubject ? { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] } : {}}
                  transition={{ duration: 0.8 }}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-body text-[#0A0A0A] min-h-[42px] flex items-center"
                >
                  {showSubject
                    ? <TypedText text="Lorem ipsum" delay={0} speed={25} />
                    : <span className="text-[#9CA3AF]">Enter subject</span>}
                </motion.div>
              </div>
              <div>
                <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">File</label>
                <div className="flex gap-2">
                  <div className="flex-1 px-3.5 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-body min-h-[42px] flex items-center">
                    {showFile
                      ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-[#0A0A0A]">xyz.pdf</motion.span>
                      : <span className="text-[#9CA3AF]">Choose file</span>}
                  </div>
                  <button className="px-4 py-2.5 rounded-lg border border-[#E5E7EB] text-[13px] font-body text-[#374151] bg-white flex items-center gap-1.5">
                    <Upload size={13} /> Browse
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="text-[13px] font-body text-[#0A0A0A] mb-1.5 block">Description<span className="text-red-500">*</span></label>
              <div className="w-full px-3.5 py-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] font-body text-[#0A0A0A] min-h-[100px] leading-relaxed">
                {showDesc
                  ? <TypedText
                      text="Lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum lorem ipsum."
                      delay={0}
                      speed={10}
                    />
                  : <span className="text-[#9CA3AF]">Placeholder</span>}
              </div>
              <p className="text-[11px] font-body text-[#9CA3AF] mt-1">Max 1,000 characters</p>
            </div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              className="flex justify-center"
            >
              <motion.button
                data-testid="report-submit-v2"
                onClick={onNext}
                animate={{
                  scale: [1, 1, 1.08, 0.97, 1.05, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 10px rgba(13,148,136,0.3)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 8px rgba(13,148,136,0.25)',
                    '0 0 0 0 rgba(13,148,136,0)',
                  ],
                }}
                transition={{ duration: 2.4, delay: 9.5, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
                className="flex items-center gap-2 px-12 py-3 rounded-xl bg-teal-600 text-white font-body font-bold text-[13px] hover:bg-teal-700"
              >
                Submit <ArrowRight size={15} />
              </motion.button>
            </motion.div>
          </motion.section>
        </div>
      </div>
    </PortalShellV2>
  );
}
